import React, { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Mail, Phone, MapPin, Send, CheckCircle, AlertCircle, Sparkles } from 'lucide-react';
import { contactData } from '../data/mock';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const INQUIRY_TYPES = [
  ['general', 'General Inquiry'],
  ['event_dialogue', 'Event / Dialogue'],
  ['sponsorship', 'Sponsorship & Partnership'],
  ['membership', 'Membership Question'],
  ['media', 'Media'],
  ['other', 'Other']
];

const Engage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const interestTitle = searchParams.get('interest');
  const dialogueNo = searchParams.get('dialogue');
  const presetType = searchParams.get('type');
  const interestMode = !!interestTitle;

  const [formData, setFormData] = useState(() => ({
    name: '',
    email: '',
    organization: '',
    partnershipType: (!interestTitle && presetType && INQUIRY_TYPES.some(([v]) => v === presetType)) ? presetType : '',
    message: interestTitle ? `I would like to register my interest in: ${interestTitle}.\n\n` : ''
  }));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
    if (error) setError('');
  };

  const handleSelectChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      partnershipType: value
    }));
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/submit`, {
        name: formData.name,
        email: formData.email,
        organization: formData.organization || (interestMode ? 'Individual' : ''),
        partnership_type: interestMode ? 'dialogue_interest' : formData.partnershipType,
        message: formData.message,
        dialogue_no: interestMode ? dialogueNo : null
      });

      if (response.data.success) {
        setIsSubmitted(true);
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({
            name: '',
            email: '',
            organization: '',
            partnershipType: '',
            message: ''
          });
        }, 5000);
      }
    } catch (err) {
      console.error('Form submission error:', err);
      setError(
        err.response?.data?.detail ||
        'Failed to submit your inquiry. Please try again or contact us directly via email.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      <section className="bg-gradient-to-br from-[#1E3A5F] to-[#1E3A5F]/90 text-white py-20 pt-32">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" data-testid="engage-page-title">
            {interestMode ? 'Register Your Interest' : 'Contact CSED'}
          </h1>
          <p className="text-lg text-gray-200 max-w-3xl mx-auto">
            {interestMode
              ? 'Let us know you are interested — we will notify you as soon as registration opens.'
              : 'Reach out for general inquiries, events, sponsorship & partnership, membership questions or media.'}
          </p>
        </div>
      </section>

      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle className="text-2xl text-[#1E3A5F]" data-testid="engage-form-title">
                    {interestMode ? 'Register Your Interest' : 'Get In Touch'}
                  </CardTitle>
                  <CardDescription>
                    {interestMode
                      ? 'Share your details and we will keep you informed about this Dialogue.'
                      : "Fill out the form below and we'll get back to you within 48 hours."}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {interestMode && (
                    <div className="mb-6 p-4 rounded-lg bg-[#E87722]/10 border border-[#E87722]/30 flex items-start gap-3" data-testid="interest-dialogue-banner">
                      <Sparkles className="text-[#E87722] flex-shrink-0 mt-0.5" size={20} />
                      <div>
                        <p className="text-xs font-semibold text-[#E87722] uppercase tracking-wide mb-0.5">Registering interest in</p>
                        <p className="font-semibold text-[#1E3A5F]">{interestTitle}</p>
                      </div>
                    </div>
                  )}
                  {isSubmitted ? (
                    <div className="text-center py-8" data-testid="engage-success-message">
                      <CheckCircle className="mx-auto text-green-600 mb-4" size={48} />
                      <h3 className="text-xl font-semibold text-[#1E3A5F] mb-2">
                        Thank You!
                      </h3>
                      <p className="text-gray-600">
                        {interestMode
                          ? 'Your interest has been recorded. We will email you as soon as registration opens for this Dialogue.'
                          : "Your inquiry has been submitted successfully. We'll be in touch soon. Please check your email for a confirmation."}
                      </p>
                      {interestMode && (
                        <div className="mt-6 text-left bg-[#1E3A5F]/5 border border-[#1E3A5F]/10 rounded-lg p-5" data-testid="membership-pitch-card">
                          <p className="font-bold text-[#1E3A5F] mb-2">Become a CSED Member & get more</p>
                          <ul className="text-sm text-gray-700 space-y-1.5 list-disc pl-5">
                            <li><strong>25% lower member pricing</strong> on this and every CSED event</li>
                            <li><strong>First intimation</strong> the moment registration opens</li>
                            <li>Curated reading materials for events you're interested in</li>
                            <li>Full access to the Members-Only Research Library</li>
                            <li>Engagements with corporates, NGOs & funding agencies for assignments</li>
                          </ul>
                          <Button className="mt-4 bg-[#1E3A5F] hover:bg-[#16304f] text-white" onClick={() => navigate('/membership')} data-testid="become-member-btn">
                            Explore Membership
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      {error && (
                        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
                          <AlertCircle className="text-red-600 flex-shrink-0 mt-0.5" size={20} />
                          <p className="text-sm text-red-800" data-testid="engage-error-message">{error}</p>
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label htmlFor="name">Full Name *</Label>
                        <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Your name"
                          className="border-gray-300 focus:border-[#E87722] focus:ring-[#E87722]"
                          disabled={isSubmitting}
                          data-testid="engage-name-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">Email Address *</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="your@email.com"
                          className="border-gray-300 focus:border-[#E87722] focus:ring-[#E87722]"
                          disabled={isSubmitting}
                          data-testid="engage-email-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="organization">Organization {interestMode ? '(optional)' : '*'}</Label>
                        <Input
                          id="organization"
                          name="organization"
                          value={formData.organization}
                          onChange={handleChange}
                          required={!interestMode}
                          placeholder="Your organization"
                          className="border-gray-300 focus:border-[#E87722] focus:ring-[#E87722]"
                          disabled={isSubmitting}
                          data-testid="engage-organization-input"
                        />
                      </div>

                      {!interestMode && (
                        <div className="space-y-2">
                          <Label htmlFor="partnershipType">Nature of Inquiry *</Label>
                          <Select
                            value={formData.partnershipType}
                            onValueChange={handleSelectChange}
                            required
                            disabled={isSubmitting}
                          >
                            <SelectTrigger className="border-gray-300 focus:border-[#E87722] focus:ring-[#E87722]" data-testid="engage-inquiry-type-select">
                              <SelectValue placeholder="Select nature of inquiry" />
                            </SelectTrigger>
                            <SelectContent>
                              {INQUIRY_TYPES.map(([value, label]) => (
                                <SelectItem key={value} value={value} data-testid={`inquiry-type-${value}`}>{label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label htmlFor="message">Message *</Label>
                        <Textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          required
                          rows={5}
                          placeholder={interestMode
                            ? 'Anything you would like us to know (role, expectations, questions)...'
                            : "Tell us about your inquiry and how you'd like to collaborate..."}
                          className="border-gray-300 focus:border-[#E87722] focus:ring-[#E87722]"
                          disabled={isSubmitting}
                          data-testid="engage-message-input"
                        />
                      </div>

                      <Button
                        type="submit"
                        className="w-full bg-[#E87722] hover:bg-[#E87722]/90 text-white transition-all duration-300 hover:scale-105"
                        size="lg"
                        disabled={isSubmitting}
                        data-testid="engage-submit-btn"
                      >
                        {isSubmitting ? (
                          <>
                            <span className="animate-spin mr-2">⏳</span>
                            Submitting...
                          </>
                        ) : (
                          <>
                            <Send size={18} className="mr-2" />
                            {interestMode ? 'Register My Interest' : 'Submit Inquiry'}
                          </>
                        )}
                      </Button>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>

            <div className="space-y-6">
              <div>
                <h2 className="text-3xl font-bold text-[#1E3A5F] mb-6">
                  Get In Touch
                </h2>
                <p className="text-gray-600 leading-relaxed mb-8">
                  We welcome inquiries from government bodies, CSR institutions,
                  enterprises, and academic organizations interested in strengthening
                  India's development architecture through collaborative partnerships.
                </p>
              </div>

              <div className="space-y-4">
                <Card className="border-l-4 border-[#E87722] hover:shadow-lg transition-shadow">
                  <CardContent className="flex items-start space-x-4 p-6">
                    <div className="p-3 bg-[#E87722]/10 rounded-lg">
                      <Mail className="text-[#E87722]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1E3A5F] mb-1">Email</h3>
                      <a
                        href={`mailto:${contactData.email}`}
                        className="text-gray-600 hover:text-[#E87722] transition-colors"
                      >
                        {contactData.email}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-[#E87722] hover:shadow-lg transition-shadow">
                  <CardContent className="flex items-start space-x-4 p-6">
                    <div className="p-3 bg-[#E87722]/10 rounded-lg">
                      <Phone className="text-[#E87722]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1E3A5F] mb-1">Phone</h3>
                      <a
                        href={`tel:${contactData.phone}`}
                        className="text-gray-600 hover:text-[#E87722] transition-colors"
                      >
                        {contactData.phone}
                      </a>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-l-4 border-[#E87722] hover:shadow-lg transition-shadow">
                  <CardContent className="flex items-start space-x-4 p-6">
                    <div className="p-3 bg-[#E87722]/10 rounded-lg">
                      <MapPin className="text-[#E87722]" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#1E3A5F] mb-1">
                        Location
                      </h3>
                      <p className="text-gray-600">{contactData.address}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-gradient-to-br from-[#1E3A5F] to-[#1E3A5F]/90 text-white">
                <CardHeader>
                  <CardTitle>How You Can Engage</CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <span className="text-[#E87722] mr-2">•</span>
                      <span>Leadership Dialogues & Events</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#E87722] mr-2">•</span>
                      <span>Sponsorship & Institutional Partnerships</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#E87722] mr-2">•</span>
                      <span>Research Collaboration</span>
                    </li>
                    <li className="flex items-start">
                      <span className="text-[#E87722] mr-2">•</span>
                      <span>Advisory Engagements & Volunteer Expertise</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Engage;
